<template>
  <div class="modal-overlay">
    <div class="mmodal-content" style="max-width: 80vw">
      <div class="modal-header">
        <h3>{{ editingRecord ? "Edit" : "Add" }} Orthopedic Assessment</h3>
        <button type="button" class="close" @click="closeModal">&times;</button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveRecord">
          <div class="form-grid">
            <div class="form-group">
              <label>Date/Time Assessed *</label>
              <input
                type="datetime-local"
                v-model="form.assessed_at"
                required
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label>Affected Site *</label>
              <input
                type="text"
                v-model="form.affected_site"
                placeholder="e.g. Left Knee, Lumbar Spine"
                required
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label>Laterality</label>
              <select v-model="form.laterality" class="form-control">
                <option value="">Select</option>
                <option value="Left">Left</option>
                <option value="Right">Right</option>
                <option value="Bilateral">Bilateral</option>
                <option value="N/A">N/A</option>
              </select>
            </div>

            <div class="form-group">
              <label>Onset</label>
              <select v-model="form.onset" class="form-control">
                <option value="">Select</option>
                <option value="Acute">Acute</option>
                <option value="Chronic">Chronic</option>
                <option value="Acute on Chronic">Acute on Chronic</option>
              </select>
            </div>

            <div class="form-group">
              <label>Mechanism of Injury</label>
              <input
                type="text"
                v-model="form.mechanism_of_injury"
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label>Pain Score (0-10)</label>
              <input
                type="number"
                v-model="form.pain_score"
                min="0"
                max="10"
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.swelling" />
                Swelling
              </label>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.deformity" />
                Deformity
              </label>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.bruising" />
                Bruising
              </label>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.open_wound" />
                Open Wound
              </label>
            </div>

            <div class="form-group">
              <label>Range of Motion</label>
              <input
                type="text"
                v-model="form.range_of_motion"
                placeholder="e.g. Knee flexion 0-90°"
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label>Gait</label>
              <select v-model="form.gait" class="form-control">
                <option value="">Select</option>
                <option value="Normal">Normal</option>
                <option value="Antalgic">Antalgic</option>
                <option value="Non-weight bearing">Non-weight bearing</option>
                <option value="Assisted">Assisted</option>
              </select>
            </div>

            <div class="form-group">
              <label>Muscle Power</label>
              <input
                type="text"
                v-model="form.muscle_power"
                placeholder="e.g. MRC 4/5"
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label>Neurovascular Status</label>
              <input
                type="text"
                v-model="form.neurovascular_status"
                placeholder="e.g. Sensation intact, pulses palpable"
                class="form-control"
              />
            </div>

            <div class="form-group full-width">
              <label>Special Tests</label>
              <textarea
                v-model="form.special_tests"
                class="form-control"
                rows="2"
              ></textarea>
            </div>

            <div class="form-group full-width">
              <label>Imaging Summary</label>
              <textarea
                v-model="form.imaging_summary"
                class="form-control"
                rows="2"
              ></textarea>
            </div>

            <div class="form-group full-width">
              <label>Provisional Diagnosis</label>
              <textarea
                v-model="form.provisional_diagnosis"
                class="form-control"
                rows="2"
              ></textarea>
            </div>

            <div class="form-group full-width">
              <label>Treatment Plan</label>
              <textarea
                v-model="form.treatment_plan"
                class="form-control"
                rows="2"
              ></textarea>
            </div>

            <div class="form-group full-width">
              <label>Notes</label>
              <textarea
                v-model="form.notes"
                class="form-control"
                rows="3"
              ></textarea>
            </div>

            <div class="form-group">
              <label>Assessed By</label>
              <input
                type="text"
                v-model="form.assessed_by"
                class="form-control"
              />
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Cancel
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{
                saving
                  ? "Saving..."
                  : (editingRecord ? "Update" : "Save") + " Assessment"
              }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { generalApiRequest } from "../../../api/apiIndex";

export default {
  name: "OrthopedicAssessmentModal",
  props: {
    visitId: {
      type: [Number, String],
      default: null,
    },
    editRecord: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      saving: false,
      editingRecord: null,
      form: this.emptyForm(),
    };
  },
  created() {
    if (this.editRecord) {
      this.editingRecord = this.editRecord;
      this.form = {
        ...this.emptyForm(),
        ...this.editRecord,
        assessed_at: this.toLocalDateTime(this.editRecord.assessed_at),
      };
    }
  },
  methods: {
    emptyForm() {
      return {
        assessed_at: this.toLocalDateTime(new Date()),
        affected_site: "",
        laterality: "",
        onset: "",
        mechanism_of_injury: "",
        pain_score: "",
        swelling: false,
        deformity: false,
        bruising: false,
        open_wound: false,
        range_of_motion: "",
        gait: "",
        muscle_power: "",
        neurovascular_status: "",
        special_tests: "",
        imaging_summary: "",
        provisional_diagnosis: "",
        treatment_plan: "",
        notes: "",
        assessed_by: "",
      };
    },

    toLocalDateTime(value) {
      if (!value) return "";
      const d = new Date(value);
      if (isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
        d.getHours()
      )}:${pad(d.getMinutes())}`;
    },

    closeModal() {
      this.$emit("close");
    },

    async saveRecord() {
      this.saving = true;
      try {
        const payload = {
          ...this.form,
          visit_id: this.visitId,
        };

        let response;
        if (this.editingRecord) {
          response = await generalApiRequest(
            `orthopedic_assessments/${this.editingRecord.id}`,
            "PUT",
            payload,
            1
          );
        } else {
          response = await generalApiRequest(
            "orthopedic_assessments",
            "POST",
            payload,
            1
          );
        }

        if (response.success) {
          this.$emit("saved", response.data);
          this.closeModal();
        } else {
          alert(response.message || "Failed to save assessment");
        }
      } catch (error) {
        console.error("Error saving orthopedic assessment:", error);
        alert("An error occurred while saving the assessment");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.mmodal-content {
  background: white;
  border-radius: 10px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 20px;
  background: linear-gradient(135deg, #2c7a7b 0%, #285e61 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
}

.close {
  background: rgb(233, 22, 22);
  border: 1px solid rgb(182, 178, 178);
  font-size: 1.5rem;
  color: white;
  line-height: 1;
  cursor: pointer;
}

.modal-body {
  padding: 30px;
  overflow-y: auto;
  flex-grow: 1;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #495057;
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 8px;
  margin-top: 28px;
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
}

.btn {
  padding: 10px 25px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-primary {
  background: #2c7a7b;
  color: white;
}

.btn-primary:hover {
  background: #24696a;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 1200px) {
  .form-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
